import React, { useState, useEffect } from 'react';
import { Table, Button, Card, Modal, Form, Alert, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { tournamentService, userService } from '../../services/firebaseService';

const SimpleTournamentList = () => {
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [eligibility, setEligibility] = useState(null);
  const [newTournament, setNewTournament] = useState({
    name: '',
    location: '',
    date: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    description: '',
    disciplines: ['Judo'],
    tatamiCount: 1
  });

  // Récupérer tous les tournois et vérifier l'éligibilité
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Vérifier l'éligibilité de l'utilisateur pour créer des tournois
        const eligibilityData = await userService.checkTournamentEligibility();
        setEligibility(eligibilityData);
        
        // Récupérer les tournois
        const tournamentsList = await tournamentService.getAllTournaments();
        setTournaments(tournamentsList);
        
        setError(null);
      } catch (err) {
        console.error('Erreur lors de la récupération des données:', err);
        setError('Impossible de charger les tournois. Veuillez réessayer.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Gérer la création d'un nouveau tournoi
  const handleCreateTournament = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      // Validation simple
      if (!newTournament.name || !newTournament.date) {
        setError('Veuillez remplir tous les champs obligatoires.');
        setLoading(false);
        return;
      }
      
      // Créer le tournoi
      const createdTournament = await tournamentService.createTournament({
        name: newTournament.name,
        location: newTournament.location,
        date: new Date(newTournament.date),
        endDate: new Date(newTournament.endDate),
        description: newTournament.description,
        disciplines: newTournament.disciplines,
        tatamiCount: parseInt(newTournament.tatamiCount) || 1
      });
      
      // Mettre à jour la liste des tournois
      setTournaments([createdTournament, ...tournaments]);
      
      // Mettre à jour l'éligibilité
      const updatedEligibility = await userService.checkTournamentEligibility();
      setEligibility(updatedEligibility);
      
      // Réinitialiser le formulaire
      setNewTournament({
        name: '',
        location: '',
        date: new Date().toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0],
        description: '',
        disciplines: ['Judo']
      });
      
      // Fermer le modal
      setShowCreateModal(false);
      setError(null);
    } catch (err) {
      console.error('Erreur lors de la création du tournoi:', err);
      setError(err.message || 'Impossible de créer le tournoi. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="simple-tournament-list">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>Mes Tournois</h2>
        <Button 
          variant="primary" 
          onClick={() => setShowCreateModal(true)}
          disabled={eligibility && !eligibility.canCreateTournament}
        >
          Créer un tournoi
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {/* Afficher l'information sur l'abonnement */}
      {eligibility && (
        <Card className="mb-3">
          <Card.Body>
            <Card.Title>Votre abonnement</Card.Title>
            <Card.Text>
              Type d'abonnement: <strong>{eligibility.subscriptionType}</strong><br />
              Tournois créés: <strong>{eligibility.tournamentsCreated} / {eligibility.tournamentsLimit}</strong>
              {!eligibility.canCreateTournament && (
                <Alert variant="warning" className="mt-2">
                  {eligibility.hasReachedLimit 
                    ? "Vous avez atteint votre limite de tournois. Veuillez mettre à niveau votre abonnement pour en créer davantage."
                    : "Votre abonnement a expiré. Veuillez le renouveler pour créer de nouveaux tournois."
                  }
                </Alert>
              )}
            </Card.Text>
          </Card.Body>
        </Card>
      )}

      {loading && !tournaments.length ? (
        <div className="text-center my-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Chargement...</span>
          </Spinner>
        </div>
      ) : (
        <>
          {tournaments.length === 0 ? (
            <Alert variant="info">
              Vous n'avez pas encore de tournois. Créez-en un pour commencer.
            </Alert>
          ) : (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Nom</th>
                  <th>Lieu</th>
                  <th>Date</th>
                  <th>Statut</th>
                  <th>Athlètes</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tournaments.map(tournament => (
                  <tr key={tournament.id}>
                    <td>{tournament.name}</td>
                    <td>{tournament.location}</td>
                    <td>
                      {tournament.date instanceof Date 
                        ? tournament.date.toLocaleDateString() 
                        : new Date(tournament.date).toLocaleDateString()}
                    </td>
                    <td>
                      <span className={`badge bg-${getStatusBadgeColor(tournament.status)}`}>
                        {getStatusLabel(tournament.status)}
                      </span>
                    </td>
                    <td>{tournament.athletes ? tournament.athletes.length : 0}</td>
                    <td>
                      <Link to={`/tournaments/${tournament.id}`} className="btn btn-primary btn-sm me-2">
                        Gérer
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </>
      )}

      {/* Modal de création de tournoi */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Créer un tournoi</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleCreateTournament}>
            <Form.Group className="mb-3">
              <Form.Label>Nom du tournoi *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Nom du tournoi"
                value={newTournament.name}
                onChange={(e) => setNewTournament({...newTournament, name: e.target.value})}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Lieu</Form.Label>
              <Form.Control
                type="text"
                placeholder="Lieu du tournoi"
                value={newTournament.location}
                onChange={(e) => setNewTournament({...newTournament, location: e.target.value})}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Date de début *</Form.Label>
              <Form.Control
                type="date"
                value={newTournament.date}
                onChange={(e) => setNewTournament({...newTournament, date: e.target.value})}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Date de fin *</Form.Label>
              <Form.Control
                type="date"
                value={newTournament.endDate}
                onChange={(e) => setNewTournament({...newTournament, endDate: e.target.value})}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Description du tournoi"
                value={newTournament.description}
                onChange={(e) => setNewTournament({...newTournament, description: e.target.value})}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Nombre de tatamis/rings</Form.Label>
              <Form.Control
                type="number"
                min="1"
                max="20"
                value={newTournament.tatamiCount}
                onChange={(e) => setNewTournament({...newTournament, tatamiCount: parseInt(e.target.value) || 1})}
                required
              />
              <Form.Text className="text-muted">
                Nombre de surfaces de combat disponibles pour ce tournoi
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Disciplines</Form.Label>
              <div>
                <Form.Check
                  type="checkbox"
                  label="Judo"
                  checked={newTournament.disciplines.includes('Judo')}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setNewTournament({...newTournament, disciplines: [...newTournament.disciplines, 'Judo']});
                    } else {
                      setNewTournament({...newTournament, disciplines: newTournament.disciplines.filter(d => d !== 'Judo')});
                    }
                  }}
                />
                <Form.Check
                  type="checkbox"
                  label="Jiu-Jitsu"
                  checked={newTournament.disciplines.includes('Jiu-Jitsu')}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setNewTournament({...newTournament, disciplines: [...newTournament.disciplines, 'Jiu-Jitsu']});
                    } else {
                      setNewTournament({...newTournament, disciplines: newTournament.disciplines.filter(d => d !== 'Jiu-Jitsu')});
                    }
                  }}
                />
                <Form.Check
                  type="checkbox"
                  label="MMA"
                  checked={newTournament.disciplines.includes('MMA')}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setNewTournament({...newTournament, disciplines: [...newTournament.disciplines, 'MMA']});
                    } else {
                      setNewTournament({...newTournament, disciplines: newTournament.disciplines.filter(d => d !== 'MMA')});
                    }
                  }}
                />
                <Form.Check
                  type="checkbox"
                  label="Kickboxing"
                  checked={newTournament.disciplines.includes('Kickboxing')}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setNewTournament({...newTournament, disciplines: [...newTournament.disciplines, 'Kickboxing']});
                    } else {
                      setNewTournament({...newTournament, disciplines: newTournament.disciplines.filter(d => d !== 'Kickboxing')});
                    }
                  }}
                />
                <Form.Check
                  type="checkbox"
                  label="Muaithai"
                  checked={newTournament.disciplines.includes('Muaithai')}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setNewTournament({...newTournament, disciplines: [...newTournament.disciplines, 'Muaithai']});
                    } else {
                      setNewTournament({...newTournament, disciplines: newTournament.disciplines.filter(d => d !== 'Muaithai')});
                    }
                  }}
                />
                <Form.Check
                  type="checkbox"
                  label="K1"
                  checked={newTournament.disciplines.includes('K1')}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setNewTournament({...newTournament, disciplines: [...newTournament.disciplines, 'K1']});
                    } else {
                      setNewTournament({...newTournament, disciplines: newTournament.disciplines.filter(d => d !== 'K1')});
                    }
                  }}
                />
              </div>
            </Form.Group>

            <div className="d-flex justify-content-end">
              <Button variant="secondary" className="me-2" onClick={() => setShowCreateModal(false)}>
                Annuler
              </Button>
              <Button variant="primary" type="submit" disabled={loading}>
                {loading ? 'Création...' : 'Créer'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

// Fonctions utilitaires
const getStatusLabel = (status) => {
  switch (status) {
    case 'draft': return 'Brouillon';
    case 'in_progress': return 'En cours';
    case 'completed': return 'Terminé';
    case 'cancelled': return 'Annulé';
    default: return 'Inconnu';
  }
};

const getStatusBadgeColor = (status) => {
  switch (status) {
    case 'draft': return 'secondary';
    case 'in_progress': return 'primary';
    case 'completed': return 'success';
    case 'cancelled': return 'danger';
    default: return 'info';
  }
};

export default SimpleTournamentList;
